import React, { useState } from "react";
import { Box, Typography, Container, Grid, Paper, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import PetsIcon from "@mui/icons-material/Pets";
import WeekendIcon from "@mui/icons-material/Weekend";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import { ServiceDialog } from "./ServiceDialog.tsx";
import dagopvang from "../assets/images/dagopvang.jpg";
import weekendopvang from "../assets/images/weekendopvang.jpg";
import vakantieopvang from "../assets/images/vakantieopvang.jpg";

// Styled components
const StyledServicesSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
  backgroundColor: theme.palette.background.default,
}));

const StyledServicesContainer = styled(Container)(({ theme }) => ({
  maxWidth: 'lg',
}));

const StyledServicesTitle = styled(Typography)<{ component?: React.ElementType }>(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(2),
}));

const StyledServiceCard = styled(Paper)<{ $isDarkMode: boolean }>(({ theme, $isDarkMode }) => ({
  padding: theme.spacing(4),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'transform 0.3s, box-shadow 0.3s',
  backgroundColor: $isDarkMode ? '#3D3D3D' : theme.palette.background.paper,
  color: $isDarkMode ? '#F0F0F0' : theme.palette.text.primary,
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[6],
    backgroundColor: $isDarkMode ? '#4A4A4A' : theme.palette.background.paper,
  },
}));

const StyledServiceIcon = styled(Box)<{ $isDarkMode: boolean }>(({ theme, $isDarkMode }) => ({
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(2),
  '& svg': {
    color: $isDarkMode ? theme.palette.primary.light : theme.palette.primary.main,
  },
}));

const StyledServiceTitle = styled(Typography)<{ component?: React.ElementType }>(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const StyledServiceDescription = styled(Typography)(({ theme }) => ({
  // Add any specific styling for service descriptions
}));

// Extended service type with image and detailed description
interface Service {
  title: string;
  description: string;
  icon: React.ReactNode;
  detailedDescription: string;
  image: any;
}

export const ServicesSection: React.FC = () => {
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const handleOpenDialog = (service: Service) => {
    setSelectedService(service);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const services: Service[] = [
    {
      title: "Feestdagopvang",
      description:
        "Professionele zorg voor uw huisdier tijdens feestdagen. Wij zorgen voor voldoende beweging, aandacht en plezier.",
      icon: <PetsIcon fontSize="large" aria-hidden="true" />,
      detailedDescription:
        "Onze feestdagopvang biedt een veilige en stimulerende omgeving voor uw huisdier tijdens feestdagen wanneer u andere verplichtingen heeft. We zorgen voor regelmatige wandelingen, speeltijd, en persoonlijke aandacht. Uw huisdier zal genieten van socialisatie met andere dieren en professionele verzorging gedurende de dag. We houden rekening met specifieke behoeften, dieetwensen en medicatie indien nodig.",
      image: dagopvang,
    },
    {
      title: "Weekendopvang",
      description:
        "Moet u een weekend weg? Uw huisdier is bij ons in goede handen met persoonlijke aandacht en verzorging.",
      icon: <WeekendIcon fontSize="large" aria-hidden="true" />,
      detailedDescription:
        "Onze weekendopvang is perfect voor als u een kort tripje maakt. Uw huisdier verblijft in een huiselijke omgeving waar het alle aandacht krijgt die het verdient. We bieden een gestructureerde routine met voldoende beweging, speeltijd en rust. Uw huisdier zal zich snel thuis voelen in onze comfortabele accommodatie met toegang tot een veilige buitenruimte.",
      image: weekendopvang,
    },
    {
      title: "Vakantieopvang",
      description:
        "Geniet zorgeloos van uw vakantie terwijl wij uw huisdier verwennen in een veilige en liefdevolle omgeving.",
      icon: <FlightTakeoffIcon fontSize="large" aria-hidden="true" />,
      detailedDescription:
        "Onze vakantieopvang zorgt ervoor dat u met een gerust hart op vakantie kunt gaan. Uw huisdier krijgt bij ons alle zorg en aandacht die het nodig heeft. We bieden een volledig verzorgd verblijf met dagelijkse wandelingen, speeltijd, en veel persoonlijke aandacht. We sturen u regelmatig updates en foto's zodat u kunt zien hoe goed uw huisdier het bij ons heeft. Bij terugkomst treft u een blije en uitgeruste viervoeter aan.",
      image: vakantieopvang,
    },
  ];

  return (
    <StyledServicesSection
      id="services"
      component="section"
      aria-labelledby="services-heading"
    >
      <StyledServicesContainer>
        <StyledServicesTitle 
          variant="h3" 
          component="h2" 
          id="services-heading"
        >
          Onze Diensten
        </StyledServicesTitle>

        <Grid container spacing={4} role="list">
          {services.map((service, index) => (
            <Grid item xs={12} md={4} key={index} role="listitem">
              <StyledServiceCard
                $isDarkMode={isDarkMode}
                elevation={3}
                onClick={() => handleOpenDialog(service)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleOpenDialog(service);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Meer informatie over ${service.title}`}
              >
                <StyledServiceIcon $isDarkMode={isDarkMode}>
                  {service.icon}
                </StyledServiceIcon>
                <StyledServiceTitle variant="h5" component="h3">
                  {service.title}
                </StyledServiceTitle>
                <StyledServiceDescription>{service.description}</StyledServiceDescription>
              </StyledServiceCard>
            </Grid>
          ))}
        </Grid>
      </StyledServicesContainer>

      {/* Service Dialog */}
      <ServiceDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        service={selectedService}
      />
    </StyledServicesSection>
  );
};
